import { create } from "zustand";

import {
  ALL_GROUPS,
  ConsentGroup,
  readConsent,
  saveConsent,
  setConsentPanelOpener,
  subscribeConsent,
  type ConsentState,
} from "@/lib/consent";
import { syncGtmWithConsent } from "@/lib/gtm";
import { syncUtmWithConsent } from "@/lib/utm";

interface ConsentStore extends ConsentState {
  /** Whether our own preferences panel is expanded. Unused under OneTrust. */
  panelOpen: boolean;
  /**
   * Switch positions while the panel is open. Held here rather than in the
   * banner because the banner renders `null` once a decision exists instead of
   * unmounting: component state would then still hold the pre-decision set, and
   * saving from a reopened panel would drop groups the visitor had accepted.
   */
  draft: ReadonlySet<ConsentGroup>;
  /** Persist the selection and update every subscriber (banner, GTM, Intercom, …). */
  save: (groups: Iterable<ConsentGroup>) => void;
  setDraftGroup: (group: ConsentGroup, granted: boolean) => void;
  openPanel: () => void;
  closePanel: () => void;
}

const initial = readConsent();

/** First visit: all groups on. Returning visit: whatever they last saved. */
function panelDraft(state: ConsentState): Set<ConsentGroup> {
  return new Set(state.decided ? state.granted : ALL_GROUPS);
}

/**
 * Shared, reactive consent state so the banner, GTM and Intercom all react to a
 * change without a page reload — including changes made in OneTrust's own
 * preference center, which reach us through `subscribeConsent`.
 */
export const useConsent = create<ConsentStore>(set => ({
  ...initial,
  panelOpen: false,
  draft: panelDraft(initial),
  save: groups => {
    saveConsent(groups);
    set({ panelOpen: false });
  },
  setDraftGroup: (group, granted) =>
    set(state => {
      const draft = new Set(state.draft);
      if (granted) draft.add(group);
      else draft.delete(group);
      return { draft };
    }),
  // Seeded on every open, so the panel shows what is actually saved and unsaved
  // edits from a previous open never survive. Undecided visitors get every
  // group on — opt-out by turning switches off, not opt-in from a blank form.
  openPanel: () => set(state => ({ panelOpen: true, draft: panelDraft(state) })),
  closePanel: () => set({ panelOpen: false }),
}));

// `saveConsent` notifies synchronously, so our own writes land here too and the
// store never holds a value the provider disagrees with.
subscribeConsent(state => {
  useConsent.setState(state);
  syncGtmWithConsent(state);
  syncUtmWithConsent(state);
});

// GTM has no server-rendered snippet any more, so the initial resolution has to
// happen here — including for returning visitors who consented on a past visit.
syncGtmWithConsent(initial);
// Catches a stored capture that outlived its consent — withdrawn in a previous
// page view, or on a surface that had not loaded this module yet.
syncUtmWithConsent(initial);

setConsentPanelOpener(() => useConsent.getState().openPanel());

/**
 * Reactive equivalent of `hasConsent`. Necessary is always granted; every other
 * group needs an explicit decision.
 */
export function useHasConsent(group: ConsentGroup): boolean {
  return useConsent(state => (group === ConsentGroup.Necessary ? true : state.decided && state.granted.has(group)));
}
